class BoxingRingProductMediaRotator {
  constructor(root) {
    this.root = root;
    this.product = document.querySelector('product-info[data-main-product]');
    this.gallery = this.product?.querySelector('.main-product__media');
    this.mediaWrapper = this.gallery?.parentElement;
    this.image = this.product?.querySelector('.main-product__media img');
    this.slides = [];
    this.activeIndex = 0;
    this.timer = null;
    this.fadeTimer = null;
    this.interval = 4500;
    this.isPaused = false;
    this.thumbnailButtons = [];

    this.#init();
  }

  #init() {
    if (!this.product || !this.gallery || !this.image) {
      return;
    }

    const originalSlide = {
      src: this.image.currentSrc || this.image.src,
      srcset: this.image.getAttribute('srcset') || '',
      alt: this.image.getAttribute('alt') || '',
    };

    this.slides = this.#getSlides(originalSlide);

    if (this.slides.length < 2) {
      return;
    }

    this.image.style.transition = 'opacity 180ms ease';
    this.gallery.classList.add('brp-product-rotator');
    this.mediaWrapper?.classList.add('brp-product-rotator-wrapper');
    document.documentElement.classList.add('brp-ring-product-page');
    this.#renderControls();
    this.#renderThumbnails();
    this.#updateThumbnails();

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#start();
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.#stop();
        return;
      }

      this.#start();
    });
  }

  #getSlides(originalSlide) {
    const slides = [];
    const productImages = Array.from(this.product.querySelectorAll('.main-product__media img'));
    const triggers = Array.from(this.root.querySelectorAll('[data-brp-open][data-brp-image]'));
    const heroImages = Array.from(this.root.querySelectorAll('[data-brp-slide]'));
    const seen = new Set();

    this.#addSlide(slides, seen, originalSlide);

    for (const image of productImages) {
      const src = image.currentSrc || image.src || image.getAttribute('src');

      this.#addSlide(slides, seen, {
        src,
        srcset: image.getAttribute('srcset') || '',
        alt: image.getAttribute('alt') || originalSlide.alt,
      });
    }

    for (const image of heroImages) {
      const src = image.currentSrc || image.src || image.getAttribute('src');

      this.#addSlide(slides, seen, {
        src,
        srcset: image.getAttribute('srcset') || '',
        alt: image.getAttribute('alt') || originalSlide.alt,
      });
    }

    for (const trigger of triggers) {
      const src = trigger.getAttribute('data-brp-image');

      this.#addSlide(slides, seen, {
        src,
        srcset: '',
        alt: trigger.getAttribute('data-brp-title') || trigger.getAttribute('aria-label') || this.image?.alt || '',
      });
    }

    return slides;
  }

  #addSlide(slides, seen, slide) {
    const key = this.#getImageKey(slide.src);

    if (!key || seen.has(key)) {
      return;
    }

    seen.add(key);
    slides.push(slide);
  }

  #getImageKey(src) {
    if (!src) {
      return '';
    }

    try {
      const url = new URL(src, window.location.origin);
      return url.pathname;
    } catch (error) {
      return src.split('?')[0];
    }
  }

  #renderControls() {
    const controls = document.createElement('div');
    controls.className = 'brp-product-rotator__controls';

    const prevButton = this.#createButton('Previous portfolio image', 'prev', this.#getArrowIcon());
    const nextButton = this.#createButton('Next portfolio image', 'next', this.#getArrowIcon());

    prevButton.addEventListener('click', () => this.#handleManualStep(-1));
    nextButton.addEventListener('click', () => this.#handleManualStep(1));

    controls.append(prevButton, nextButton);
    this.gallery.appendChild(controls);
  }

  #renderThumbnails() {
    const thumbnails = document.createElement('div');
    thumbnails.className = 'brp-product-rotator__thumbs';
    thumbnails.setAttribute('aria-label', 'Product and portfolio image thumbnails');

    this.thumbnailButtons = this.slides.map((slide, index) => {
      const button = document.createElement('button');
      const image = document.createElement('img');

      button.type = 'button';
      button.className = 'brp-product-rotator__thumb';
      button.setAttribute('aria-label', `Show image ${index + 1}`);
      button.addEventListener('click', () => {
        this.#pause();
        this.#showSlide(index);
      });

      image.src = slide.src;
      image.alt = '';
      image.loading = 'lazy';

      button.appendChild(image);
      thumbnails.appendChild(button);

      return button;
    });

    if (this.mediaWrapper) {
      this.mediaWrapper.appendChild(thumbnails);
      return;
    }

    this.gallery.after(thumbnails);
  }

  #createButton(label, variant, icon) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `brp-product-rotator__button brp-product-rotator__button--${variant}`;
    button.setAttribute('aria-label', label);
    button.innerHTML = icon;

    return button;
  }

  #getArrowIcon() {
    return `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M9 4L17 12L9 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="square" stroke-linejoin="square"></path>
      </svg>
    `;
  }

  #handleManualStep(direction) {
    this.#pause();
    this.#showSlide(this.activeIndex + direction);
  }

  #rotate() {
    this.#showSlide(this.activeIndex + 1);
  }

  #showSlide(index) {
    this.activeIndex = (index + this.slides.length) % this.slides.length;
    const slide = this.slides[this.activeIndex];

    if (!slide) {
      return;
    }

    if (this.fadeTimer !== null) {
      window.clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }

    this.image.style.opacity = '0';

    this.fadeTimer = window.setTimeout(() => {
      if (slide.srcset) {
        this.image.setAttribute('srcset', slide.srcset);
      } else {
        this.image.removeAttribute('srcset');
      }

      this.image.src = slide.src;
      this.image.alt = slide.alt;
      this.image.style.opacity = '1';
      this.#updateThumbnails();
      this.fadeTimer = null;
    }, 180);
  }

  #updateThumbnails() {
    for (const [index, button] of this.thumbnailButtons.entries()) {
      const isActive = index === this.activeIndex;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
    }
  }

  #start() {
    if (this.isPaused || this.timer !== null) {
      return;
    }

    this.timer = window.setInterval(() => this.#rotate(), this.interval);
  }

  #pause() {
    this.isPaused = true;
    this.#stop();
  }

  #stop() {
    if (this.timer === null) {
      return;
    }

    window.clearInterval(this.timer);
    this.timer = null;
  }

}

const initBoxingRingProductMediaRotators = () => {
  const sections = document.querySelectorAll('[data-brp]');

  for (const section of sections) {
    if (section.dataset.brpProductRotatorInited === 'true') {
      continue;
    }

    section.dataset.brpProductRotatorInited = 'true';
    new BoxingRingProductMediaRotator(section);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBoxingRingProductMediaRotators);
} else {
  initBoxingRingProductMediaRotators();
}

document.addEventListener('shopify:section:load', initBoxingRingProductMediaRotators);
